import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import MovieCard from "@/components/feature/MovieCard";
import SearchField from "@/components/feature/SearchField";
import { useSeo } from "@/hooks/useSeo";
import { getHotMovies, paginate, searchMovies, type SortKey } from "@/lib/movie";
import { hotSearches } from "@/mocks/movies";

const searchSorts: { key: SortKey; label: string }[] = [
  { key: "relevance", label: "相关度" },
  { key: "updated", label: "最近更新" },
  { key: "hot", label: "热门度" },
  { key: "rating", label: "评分最高" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const sort = (searchParams.get("sort") || "relevance") as SortKey;
  const page = Number(searchParams.get("page") || 1);

  const results = useMemo(
    () => (keyword ? searchMovies(keyword, sort) : []),
    [keyword, sort]
  );
  const paged = paginate(results, Number.isNaN(page) ? 1 : page);
  const hotMovies = getHotMovies(4);

  useSeo({
    title: keyword
      ? `“${keyword}”的搜索结果 - 蜜桃成熟时在线观看`
      : "搜索影片 - 蜜桃成熟时在线观看",
    description: keyword
      ? `在蜜桃成熟时在线观看搜索“${keyword}”，共找到 ${results.length} 部相关影片，支持按相关度、更新时间、热门度与评分排序。`
      : "输入影片名称、导演或类型关键词，快速在蜜桃成熟时在线观看的片库中找到想看的电影。",
    keywords: `${keyword || "搜索电影"},蜜桃成熟时在线观看,在线观看电影,高清电影`,
  });

  const changeSort = (nextSort: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (nextSort === "relevance") {
      next.delete("sort");
    } else {
      next.set("sort", nextSort);
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(nextPage));
    }
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `“${keyword}”的搜索结果` : "搜索影片"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            支持按片名、导演、演员、类型与标签搜索，输入关键词后回车即可查看结果。
          </p>
          <div className="mt-6 max-w-3xl">
            <SearchField key={keyword} defaultValue={keyword} size="lg" />
          </div>

          {!keyword && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                热门搜索
              </span>
              {hotSearches.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setSearchParams({ q: item })}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {keyword ? (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-700">
                共找到{" "}
                <span className="font-label font-bold text-primary-600">{results.length}</span>{" "}
                部与 “{keyword}” 相关的影片
                {results.length > 0 && (
                  <span className="ml-2 text-xs text-foreground-500">
                    （第 {paged.currentPage} / {paged.totalPages} 页）
                  </span>
                )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">排序</span>
                {searchSorts.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => changeSort(option.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                      sort === option.key
                        ? "bg-secondary-900 font-medium text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {paged.items.length > 0 ? (
              <>
                <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
                  {paged.items.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
                <Pagination
                  currentPage={paged.currentPage}
                  totalPages={paged.totalPages}
                  onChange={changePage}
                />
              </>
            ) : (
              <div className="mt-8">
                <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-emotion-sad-line text-2xl w-6 h-6 flex items-center justify-center" />
                  </span>
                  <h2 className="mt-4 text-lg font-semibold text-foreground-950">
                    没有找到 “{keyword}” 相关的影片
                  </h2>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                    试试换一个更短的关键词，或者用类型浏览片库。下面也为你准备了当前热门的几部影片。
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    {hotSearches.slice(0, 4).map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setSearchParams({ q: item })}
                        className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-10">
                  <div className="flex items-end justify-between">
                    <h2 className="text-lg font-bold text-foreground-950">热门影片推荐</h2>
                    <Link
                      to="/category?sort=hot"
                      className="whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      查看热度榜
                      <i className="ri-arrow-right-line ml-1 text-sm w-4 h-4 inline-flex items-center justify-center" />
                    </Link>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
                    {hotMovies.map((movie, index) => (
                      <MovieCard key={movie.id} movie={movie} index={index} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <i className="ri-fire-line text-base w-4 h-4 flex items-center justify-center text-primary-600" />
              <h2 className="text-lg font-bold text-foreground-950">大家都在看</h2>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
              {hotMovies.map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} index={index} />
              ))}
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950">搜索小提示</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { icon: "ri-film-line", title: "按片名搜索", desc: "直接输入影片名称，例如「蜜桃初熟」。" },
                  { icon: "ri-user-star-line", title: "按主创搜索", desc: "输入导演或演员名字，可找到其相关作品。" },
                  { icon: "ri-price-tag-3-line", title: "按类型搜索", desc: "输入「悬疑」「治愈」等类型词也可匹配。" },
                ].map((tip) => (
                  <div key={tip.title} className="rounded-md bg-background-100 p-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${tip.icon} text-base w-4 h-4 flex items-center justify-center`} />
                    </span>
                    <h3 className="mt-3 text-sm font-medium text-foreground-900">{tip.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}